// Sayım farkları raporu (/stok/sayim-farklari): tamamlanan sayımlarda onaylanan fazla ve eksikler.
// Değerler onay anında saklanan sonuçlardır; sonradan değişmez.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress, FormControlLabel, InputAdornment, Link, MenuItem, Paper,
  Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../contexts/AuthContext';
import { WarehouseService, type InventoryWarehouseDto } from '../services/warehouseService';
import { fmtDate, fmtMoney, fmtQty, stockCountService, type StockCountDifferenceRow } from './stockCountService';
import { exportDifferencesExcel } from './stockCountExcel';

const toInputDate = (d: Date) => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const StockCountDifferencesReport: React.FC = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const tt = (tr: string, en: string) => ((i18n.language || '').startsWith('en') ? en : tr);
  const { userDetails } = useAuth() as any;
  const companyId: string = userDetails?.companyId || '';

  const [from, setFrom] = useState(() => { const d = new Date(); d.setDate(1); return toInputDate(d); });
  const [to, setTo] = useState(() => toInputDate(new Date()));
  const [warehouseId, setWarehouseId] = useState('');
  const [search, setSearch] = useState('');
  const [includeZero, setIncludeZero] = useState(false);
  const [warehouses, setWarehouses] = useState<InventoryWarehouseDto[]>([]);
  const [rows, setRows] = useState<StockCountDifferenceRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!companyId) return;
    WarehouseService.getAllWarehouses(companyId)
      .then((list) => setWarehouses(list.filter((w) => (w.warehouseType || 'Normal') !== 'Smart')))
      .catch(() => setWarehouses([]));
  }, [companyId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Tarihler yerel gün başı/sonu → UTC
      const fromIso = from ? new Date(`${from}T00:00:00`).toISOString() : undefined;
      const toIso = to ? new Date(`${to}T23:59:59`).toISOString() : undefined;
      setRows(await stockCountService.differences({
        from: fromIso, to: toIso, warehouseId: warehouseId || undefined, search: search.trim() || undefined, includeZero,
      }));
    } catch (e: any) {
      setError(e?.message || tt('Rapor yüklenemedi.', 'Report could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [from, to, warehouseId, search, includeZero]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const timer = setTimeout(load, 300);
    return () => clearTimeout(timer);
  }, [load]);

  const kpi = useMemo(() => {
    const surplus = rows.reduce((s, r) => s + Math.max(0, r.differenceValue ?? 0), 0);
    const shortage = rows.reduce((s, r) => s + Math.min(0, r.differenceValue ?? 0), 0);
    return { surplus, shortage, net: surplus + shortage, counts: new Set(rows.map((r) => r.countId)).size };
  }, [rows]);

  const exportExcel = async () => {
    try { await exportDifferencesExcel(rows, `${from}_${to}`); } catch (e: any) { setError(e?.message || 'Excel oluşturulamadı.'); }
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, display: 'grid', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <FactCheckIcon color="primary" sx={{ fontSize: 34 }} />
        <Typography variant="h4" fontWeight={700} sx={{ flex: 1, minWidth: 200 }}>
          {tt('Sayım Farkları', 'Count Differences')}
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/stok/sayim')}>{tt('Sayımlar', 'Counts')}</Button>
        <Button variant="contained" startIcon={<DownloadIcon />} onClick={exportExcel} disabled={rows.length === 0}>
          {tt('Excel indir', 'Download Excel')}
        </Button>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2 }}>
        {[
          { label: tt('Tamamlanan sayım', 'Completed counts'), value: String(kpi.counts), color: undefined },
          { label: tt('Sayım fazlası', 'Surplus'), value: `+${fmtMoney(kpi.surplus)}`, color: 'success.main' },
          { label: tt('Sayım eksiği', 'Shortage'), value: fmtMoney(kpi.shortage), color: 'error.main' },
          { label: tt('Net fark', 'Net difference'), value: fmtMoney(kpi.net), color: kpi.net < 0 ? 'error.main' : 'success.main' },
        ].map((k) => (
          <Card key={k.label} variant="outlined">
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="body2" color="text.secondary">{k.label}</Typography>
              <Typography variant="h5" fontWeight={700} color={k.color}>{k.value}</Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Paper variant="outlined" sx={{ p: 2, display: 'grid', gap: 1.5 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '2fr 1fr 1fr 1fr auto' }, gap: 1.5, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder={tt('Ürün, stok kodu, barkod ya da sayım no', 'Product, code, barcode or count no')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ gridColumn: { xs: '1 / -1', md: 'auto' } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
          <TextField size="small" type="date" label={tt('Başlangıç', 'From')} value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField size="small" type="date" label={tt('Bitiş', 'To')} value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField select size="small" label={tt('Depo', 'Warehouse')} value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
            <MenuItem value="">{tt('Tümü', 'All')}</MenuItem>
            {warehouses.map((w) => <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>)}
          </TextField>
          <FormControlLabel control={<Switch checked={includeZero} onChange={(e) => setIncludeZero(e.target.checked)} />} label={tt('Farksızlar da', 'Include zero')} />
        </Box>
        {error && <Alert severity="error">{error}</Alert>}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: 'primary.main', color: 'primary.contrastText', fontWeight: 700 } }}>
                <TableCell>{tt('Onay', 'Approved')}</TableCell>
                <TableCell>{tt('Sayım', 'Count')}</TableCell>
                <TableCell>{tt('Depo', 'Warehouse')}</TableCell>
                <TableCell>{tt('Ürün', 'Product')}</TableCell>
                <TableCell align="right">{tt('Beklenen', 'Expected')}</TableCell>
                <TableCell align="right">{tt('Sayılan', 'Counted')}</TableCell>
                <TableCell align="right">{tt('Fark', 'Diff.')}</TableCell>
                <TableCell align="right">{tt('Birim maliyet', 'Unit cost')}</TableCell>
                <TableCell align="right">{tt('Tutar', 'Amount')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {tt('Bu dönemde onaylanan sayım farkı yok.', 'No approved count differences in this period.')}
                </TableCell></TableRow>
              )}
              {rows.map((r, i) => {
                const d = r.differenceQty ?? 0;
                const color = d > 0 ? 'success.main' : d < 0 ? 'error.main' : 'text.secondary';
                return (
                  <TableRow key={`${r.countId}-${r.itemId}-${r.lotNo ?? ''}-${i}`} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(r.approvedAt)}</TableCell>
                    <TableCell>
                      <Link component="button" onClick={() => navigate(`/stok/sayim/${r.countId}`)}>{r.number}</Link>
                      {r.countName && <Typography variant="caption" display="block" color="text.secondary">{r.countName}</Typography>}
                    </TableCell>
                    <TableCell>{r.warehouseName}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{r.itemName}{r.lotNo ? ` · ${r.lotNo}` : ''}</Typography>
                      {(r.stockCode || r.hasManualEntry) && (
                        <Typography variant="caption" color="text.secondary">
                          {[r.stockCode, r.hasManualEntry ? tt('elle girildi', 'typed') : null].filter(Boolean).join(' · ')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{fmtQty(r.expectedQty)} {r.unit}</TableCell>
                    <TableCell align="right">{fmtQty(r.countedQty)}</TableCell>
                    <TableCell align="right" sx={{ color, fontWeight: 700 }}>{d > 0 ? '+' : ''}{fmtQty(d)}</TableCell>
                    <TableCell align="right">{fmtMoney(r.unitCost)}</TableCell>
                    <TableCell align="right" sx={{ color, fontWeight: 600 }}>{fmtMoney(r.differenceValue)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {rows.length >= 5000 && (
          <Typography variant="caption" color="text.secondary">
            {tt('İlk 5000 satır gösteriliyor; tarih aralığını daraltın.', 'Showing the first 5000 rows; narrow the date range.')}
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default StockCountDifferencesReport;
